import React, { useEffect, useState, useRef, useCallback } from "react";
import { MdArrowBackIos, MdOutlineArrowForwardIos } from "react-icons/md";
import config from "../../config";

const DebugSlider = ({ images, onImageError, onImageClick }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState(new Set([0])); // Track loaded images
    const [isLoading, setIsLoading] = useState(true);
    const imgRef = useRef(null);
    const IMG_BASE_URL = config.get("API_URL");

    const handleNext = (e) => {
        e.stopPropagation(); // Prevent parent click event
        const nextIndex = (currentIndex + 1) % images.length;
        setCurrentIndex(nextIndex);
        // Preload next image
        preloadImage(nextIndex);
    };

    const handlePrev = (e) => {
        e.stopPropagation(); // Prevent parent click event
        const prevIndex = (currentIndex - 1 + images.length) % images.length;
        setCurrentIndex(prevIndex);
        // Preload previous image
        preloadImage(prevIndex);
    };

    // Preload images for smoother transitions
    const preloadImage = useCallback((index) => {
        if (images[index] && !loadedImages.has(index)) {
            const img = new Image();
            img.src = images[index];
            img.onload = () => {
                setLoadedImages(prev => new Set([...prev, index]));
            };
        }
    }, [images, loadedImages]);

    // Preload adjacent images when current image changes
    useEffect(() => {
        if (images.length > 1) {
            // Preload next image
            const nextIndex = (currentIndex + 1) % images.length;
            preloadImage(nextIndex);
            
            // Preload previous image
            const prevIndex = (currentIndex - 1 + images.length) % images.length;
            preloadImage(prevIndex);
        }
    }, [currentIndex, images, preloadImage]);

    const handleImageLoad = () => {
        setIsLoading(false);
        setLoadedImages(prev => new Set([...prev, currentIndex]));
    };

    const handleImageError = (e) => {
        setIsLoading(false);
        e.target.style.display = "none";
        onImageError && onImageError(currentIndex, images[currentIndex]);
    };

    return (
        <div
            className="tw-relative tw-w-full tw-h-[190px] 2xl:tw-h-[220px] tw-overflow-hidden tw-rounded-tr-[3px] tw-rounded-tl-[3px]"
            onClick={onImageClick} // Trigger modal on image click
        >
            {/* Loading placeholder */}
            {isLoading && (
                <div className="tw-absolute tw-inset-0 tw-bg-gray-200 tw-animate-pulse tw-flex tw-items-center tw-justify-center">
                    <div className="tw-text-gray-400">Loading...</div>
                </div>
            )}
            
            {images.length > 0 && (
                <img
                    ref={imgRef}
                    src={`${images[currentIndex]}`}
                    alt={`Slide ${currentIndex}`}
                    className="tw-w-full tw-h-full tw-object-cover"
                    loading="lazy" // Enable browser lazy loading
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                />
            )}
            
            {/* Navigation arrows - only show if multiple images */}
            {images.length > 1 && (
                <>
                    <button
                        onClick={handlePrev}
                        className="tw-absolute tw-left-2 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black/50 tw-text-white tw-rounded-full tw-p-1 hover:tw-bg-black/70 tw-transition-colors tw-flex tw-items-center tw-justify-center"
                        aria-label="Previous image"
                    >
                        <MdArrowBackIos className="tw-w-4 tw-h-4 tw-ml-[2px]" />
                    </button>
                    <button
                        onClick={handleNext}
                        className="tw-absolute tw-right-2 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-black/50 tw-text-white tw-rounded-full tw-p-1 hover:tw-bg-black/70 tw-transition-colors tw-flex tw-items-center tw-justify-center"
                        aria-label="Next image"
                    >
                        <MdOutlineArrowForwardIos className="tw-w-4 tw-h-4" />
                    </button>
                </>
            )}
        </div>
    );
};

export default DebugSlider;
